import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router';
import authAPI from '../../api/authAPI';
import { sanitizeOtpInput } from '../../utils/sanitizeText';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%);
`;

const Card = styled.div`
  background-color: var(--bg-primary);
  padding: 2rem;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-md);
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h1`
  text-align: center;
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
`;

const Subtitle = styled.p`
  text-align: center;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgb(16 185 129 / 0.1);
  }
`;

const Button = styled.button`
  background-color: var(--primary-color);
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 1rem;
  
  &:hover {
    background-color: var(--primary-dark);
  }
  
  &:disabled {
    background-color: var(--text-light);
    cursor: not-allowed;
  }
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  color: var(--text-secondary);
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const OTP_SENT_MESSAGE =
  'We sent a 6-digit code to your phone via WhatsApp or SMS. Enter the code, then set a new password.';

const PasswordResetPage = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkConfirm = Boolean(uid && token);
  const [identifier, setIdentifier] = useState(searchParams.get('identifier') || '');
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState('request');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const autoSent = useRef(false);

  const sendReset = async (value) => {
    if (!value.trim()) {
      setError('Please enter your email or phone number');
      return;
    }

    setIdentifier(value.trim());
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const result = await authAPI.requestPasswordReset(value.trim());
      if (result?.channel === 'otp') {
        setOtpCode('');
        setStep('otp');
        setMessage(result.message || OTP_SENT_MESSAGE);
      } else {
        setMessage(result?.message || 'If this account has an email, a password reset link has been sent.');
      }
    } catch (err) {
      let errorMessage = 'Failed to send reset code. Please try again.';
      if (err.message) errorMessage = err.message;
      else if (err.response?.data?.error) errorMessage = err.response.data.error;
      if (err.status === 405 || err.response?.status === 405) {
        errorMessage = 'Password reset is currently unavailable due to server configuration. Please contact support at support@flea-marketplace.com or try again later.';
      }
      setError(errorMessage);
      console.error('Password reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const preset = (searchParams.get('identifier') || '').trim();
    if (!preset || autoSent.current || linkConfirm) return;
    autoSent.current = true;
    sendReset(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    await sendReset(identifier);
  };

  const handleOtpContinue = (e) => {
    e.preventDefault();
    if (otpCode.trim().length !== 6) {
      setError('Enter the 6-digit code we sent you');
      return;
    }
    setError(null);
    setMessage('Code accepted. Set a new password so you can sign in with it next time — no OTP needed.');
    setStep('password');
  };

  const handleConfirmOtpReset = async (e) => {
    e.preventDefault();
    if (otpCode.trim().length !== 6) {
      setError('Enter the 6-digit code we sent you');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await authAPI.confirmPasswordResetWithOtp(identifier.trim(), otpCode.trim(), newPassword);
      setMessage('Password updated. You can now sign in with your new password — no OTP needed.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reset password. The code may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await authAPI.confirmPasswordReset(uid, token, newPassword);
      setMessage('Password updated. You can now sign in with your new password — no OTP needed.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reset password. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!linkConfirm) {
    return (
      <Container>
        <Card>
          <Title>{step === 'password' ? 'Set a New Password' : 'Reset Password'}</Title>
          <Subtitle>
            {step === 'otp'
              ? OTP_SENT_MESSAGE
              : step === 'password'
                ? 'Choose a password so you can sign in with it next time. You will not need an OTP to log in.'
                : 'Enter your email for a reset link, or your phone number for a WhatsApp or SMS code.'}
          </Subtitle>
          
          {message && <SuccessMessage>{message}</SuccessMessage>}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {step === 'otp' ? (
            <Form onSubmit={handleOtpContinue}>
              <FormGroup>
                <Label htmlFor="otpCode">6-digit code</Label>
                <Input
                  type="text"
                  id="otpCode"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  autoComplete="one-time-code"
                  onChange={(e) => setOtpCode(sanitizeOtpInput(e.target.value))}
                  required
                  placeholder="123456"
                  disabled={loading}
                />
              </FormGroup>
              <Button type="submit" disabled={loading}>
                Continue
              </Button>
            </Form>
          ) : step === 'password' ? (
            <Form onSubmit={handleConfirmOtpReset}>
              <FormGroup>
                <Label htmlFor="newPasswordOtp">New Password</Label>
                <Input
                  type="password"
                  id="newPasswordOtp"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Enter new password (min 8 characters)"
                  disabled={loading}
                  minLength={8}
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="confirmPasswordOtp">Confirm Password</Label>
                <Input
                  type="password"
                  id="confirmPasswordOtp"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm new password"
                  disabled={loading}
                  minLength={8}
                />
              </FormGroup>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Password'}
              </Button>
            </Form>
          ) : (
            <Form onSubmit={handleRequestReset}>
              <FormGroup>
                <Label htmlFor="identifier">Email or phone number</Label>
                <Input
                  type="text"
                  id="identifier"
                  name="identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  placeholder="email@example.com or 01xxxxxxxxx"
                  disabled={loading}
                />
              </FormGroup>

              <Button type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send reset'}
              </Button>
            </Form>
          )}

          <BackLink>
            {step === 'otp' || step === 'password' ? (
              <button
                type="button"
                onClick={() => {
                  setStep(step === 'password' ? 'otp' : 'request');
                  setError(null);
                }}
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', font: 'inherit' }}
              >
                Back
              </button>
            ) : (
              <Link to="/login">Back to Login</Link>
            )}
          </BackLink>
        </Card>
      </Container>
    );
  }

  // Confirm step (with uid and token from URL)
  return (
    <Container>
      <Card>
        <Title>Set New Password</Title>
        <Subtitle>
          Choose a password so you can sign in with it next time. You will not need an OTP to log in.
        </Subtitle>

        {message && <SuccessMessage>{message}</SuccessMessage>}
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Form onSubmit={handleConfirmReset}>
          <FormGroup>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              type="password"
              id="newPassword"
              name="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder="Enter new password (min 8 characters)"
              disabled={loading}
              minLength={8}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
              disabled={loading}
              minLength={8}
            />
          </FormGroup>

          <Button type="submit" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Form>

        <BackLink>
          <Link to="/login">Back to Login</Link>
        </BackLink>
      </Card>
    </Container>
  );
};

export default PasswordResetPage;

